import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faChevronDown, faArrowUp, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import {
  getUserWatchlists,
  getWatchlistItems,
  getUserDefaultWatchlistItems,
  getUserDefaultWatchlist,
  getChartStockDetails,
  getParentWatchlists,
  getWatchlistsSearchPage,
  setUserDefaultWatchlist,
} from "../../../api/user";
import InlineBuySellPanel from "./InlineBuySellPanel";
import { CURATED_PARENT_ID, sortCuratedLists } from "../../Watchlist/watchlistFormat";
import { baseSymbol, instrumentName } from "../../../utils/instrumentFormat";
import { assetClass, priceDecimals } from "../../../utils/orderFormat";
import { marketStatus } from "../../../utils/marketStatus";
import { useLeagueTrade } from "../../../context/LeagueTradeContext";
import { filterEligible } from "../../../utils/leagueAssets";
import { toast } from "react-toastify";
import {
  NO_ELIGIBLE_ASSETS_MESSAGE,
  blocksUseInTrade,
  fetchEligibleCount,
  fetchEligibleCounts,
} from "../../../utils/leagueListEligibility";
import qushiLogo from "../../../images/log2.png";
import {
  getSidebarCollapseOverride,
  isFullScreenChartRoute,
  subscribeSidebarCollapse,
} from "../../../utils/sidebarCollapse.js";
import { SkeletonBlock } from "../../Skeleton/Skeleton";
import ListSkeleton from "../../Skeleton/ListSkeleton";

// PARENT ids 1/2/3 (the `parent_watchlists` table) are the "direct" asset
// categories. They're filtered out of this dropdown entirely — asset search
// moved to the symbol pill in the chart toolbar — but the ids are still needed
// to skip them when the parent hierarchy is built.
const DIRECT_PARENT_IDS = new Set([1, 2, 3]);
const isDirectParent = (id) => DIRECT_PARENT_IDS.has(Number(id));

// A user's `default_watchlist_id` is EITHER one of these three legacy string
// keys (still settable from the mobile Search screen) OR a numeric watchlist id.
// A numeric default is ALWAYS a `watchlists`.id — never a category parent id,
// even though the parent table happens to number its categories 1/2/3 as well.
// The two live in different tables. Reading a numeric default as a category is
// what made the default list (The Core is watchlist id 3 on live, and the
// column's DB default is '3') render under the title "Search Crypto".
// Titled by the asset class alone: this slot names the list that is on screen,
// so it must never read like the search action the label used to describe.
// What to call the league's OWN asset set when it is the thing on screen.
//
// Client Rev 2: "One small naming improvement I would consider is calling that
// system-generated list 'League stocks,' 'League crypto,' or 'League forex'
// instead of just 'Stocks,' especially since a future league might allow only 20
// specific stocks rather than every stock." It also keeps this header
// distinguishable from a PERSONAL list a member happened to name "Stocks", which
// matters now that the header is required to name its own source exactly.
const leagueListLabel = (lg) => {
  const category = lg?.categoryLabel;
  if (category) return `League ${category.toLowerCase()}`;
  return lg?.title || "League assets";
};

const DIRECT_LABEL_BY_KEY = {
  stocks: "Stocks",
  forex: "Forex",
  crypto: "Crypto",
};

// Subscribe to the same live-price websocket events ChartTopSummary uses so
// the right-panel price updates in real time instead of staying on the
// initial API value.
const useLivePrice = (symbol) => {
  const [livePrice, setLivePrice] = useState(null);
  useEffect(() => {
    setLivePrice(null);
  }, [symbol]);
  useEffect(() => {
    const onLive = (e) => {
      if (e.detail && typeof e.detail.price === "number") {
        setLivePrice(e.detail.price);
      }
    };
    window.addEventListener("livePriceUpdate", onLive);
    return () => window.removeEventListener("livePriceUpdate", onLive);
  }, []);
  return livePrice;
};

const getCategoryFromSymbol = (symbol) => {
  if (!symbol) return "Stocks";
  if (symbol.startsWith("X:")) return "Crypto";
  if (symbol.startsWith("C:")) return "Forex";
  return "Stocks";
};

// Prices follow the asset-class rules in utils/orderFormat: stocks 2dp, forex 4dp,
// crypto 2dp at/above $1 and 4dp below it. Chg uses the SAME precision as the
// price it belongs to, so sub-$1 crypto reads -0.0003 instead of a meaningless
// -0.00. Chg% is a percentage, always 2dp.
const formatNumber = (n, decimals = 2) => {
  if (n === null || n === undefined || n === "") return "-";
  const num = Number(n);
  if (isNaN(num)) return "-";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

// Decimals for one row / quote — derived from that symbol's own class and price,
// so BTC at 63,180 stays 2dp while TRX at 0.3258 gets 4dp in the same table.
const priceDp = (symbol, price) =>
  priceDecimals(assetClass(symbol), Number(price));

// Rendered in Last/Chg/Chg% cells while the per-row price fetch is still in
// flight. A grey block, never a "-" or "0.00%" (either reads as real data) and
// never a spinner — one neutral placeholder shape product-wide.
const PendingDot = () => (
  <SkeletonBlock height={10} width={40} className="inline-block align-middle" />
);

const ChartRightWatchlist = ({
  selectedStock,
  stockData,
  onStockSelect,
  onAddToWatchlist,
  editOrder,
  onEditOrderDone,
}) => {
  // Inside a league this panel shows the member's OWN watchlists, narrowed to
  // what the league allows. Null outside a league, where everything below
  // behaves exactly as it always has.
  //
  // It used to show the league's fixed asset list instead, and nothing else —
  // the dropdown was disabled and personal watchlists never appeared. Client,
  // "League Watchlist: launch behavior", asked for the opposite:
  //
  //   "Keep the existing QUSHi Watchlist system unchanged inside League Play.
  //    Users can continue creating and managing their normal personal
  //    watchlists… When Use in Trade is selected inside a league, only assets
  //    eligible for that league should populate the league Trade wheel… The
  //    watchlist itself is global. How it is used in Trade is contextual."
  //
  // So the panel runs the ordinary machinery — same dropdown, same lists, same
  // default — and the league only narrows what comes out of it. See
  // leagueSafeItems below, and the same narrowing on the server in
  // WatchlistController::getUserDefaultWatchlistItems, which is what feeds the
  // mobile wheel.
  const league = useLeagueTrade();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Includes /<slug>/trade — see isFullScreenChartRoute.
    if (!isFullScreenChartRoute(location.pathname)) return false;
    const override = getSidebarCollapseOverride();
    return override === null ? false : override;
  });

  useEffect(() => {
    const computeCollapsed = () => {
      if (!isFullScreenChartRoute(location.pathname)) return false;
      const override = getSidebarCollapseOverride();
      return override === null ? false : override;
    };
    setIsCollapsed(computeCollapsed());
    const unsubscribe = subscribeSidebarCollapse(() => {
      setIsCollapsed(computeCollapsed());
    });
    return unsubscribe;
  }, [location]);

  // active selection shape: { kind: "default" }
  //                       | { kind: "directKey", key: "stocks"|"forex"|"crypto", label }
  //                       | { kind: "id", id: number, label }
  const [active, setActive] = useState({ kind: "default", label: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectorOpen, setSelectorOpen] = useState(false);
  // Keyed by symbol — { "X:BTCUSD": { price, change, changePct } }
  const [priceMap, setPriceMap] = useState({});

  // Parent watchlists (top-level groups) and their children.
  const [parents, setParents] = useState([]);
  const [childrenByParent, setChildrenByParent] = useState({});

  // Eligible-asset count per list id, inside a league. Client Rev 2: "Apply the
  // same rule to the desktop Trade list dropdown. A list with zero eligible
  // assets should not be selectable as the active Trade list."
  //
  // Prefetched rather than checked on click so the rows are visibly unavailable
  // before they are pressed. A count that has not arrived yet is `undefined` and
  // blocks nothing — handleSelect asks again before it commits, so a click that
  // beats the prefetch is still refused.
  const [eligibleCounts, setEligibleCounts] = useState({});

  // Loads the curated default list ("The Core") into the panel. This is the
  // never-blank floor: it runs when the user's stored default points at a list
  // they deleted, or when they have no default at all. `persist` writes The Core
  // back as their default so every other surface stops reading a dead id too.
  // Set the panel's rows, narrowed to what this league allows.
  // Outside a league this is setItems and nothing else.
  //
  // This USED to substitute the league's own symbols whenever the filter came
  // back empty — a fallback that was ours, not the client's. Client Rev 2 has
  // now ruled on it: "Do not automatically fall back to the league default asset
  // list after an invalid selection… The header and the assets underneath must
  // always represent the same source." The substitution left `active` naming the
  // member's chosen list while the rows underneath came from somewhere else,
  // which is precisely the state they called a bug. It is gone.
  //
  // Nothing is left unshown by removing it. A list with zero eligible assets can
  // no longer be chosen at all (see eligibleCounts below and the Watchlist
  // screen's Use in Trade), and on first entry the SERVER still substitutes the
  // league's universe — but it says so with `league_assets: true`, and the
  // branch that reads that flag renames the header to match. Default because
  // nothing has been selected yet: good. Silent fallback after a selection: bad.
  const commitItems = (list) => {
    const rows = Array.isArray(list) ? list : [];
    if (!league?.game) {
      setItems(rows);
      return rows;
    }
    const kept = filterEligible(rows, league.game);
    setItems(kept);
    return kept;
  };

  const loadCoreFallback = async ({ persist = false } = {}) => {
    try {
      const res = await getWatchlistsSearchPage(CURATED_PARENT_ID);
      const lists = Array.isArray(res?.data?.watchlists) ? res.data.watchlists : [];
      // Same rule the dropdown and the Watchlist screen use: "The Core" sorts to
      // the front, so the first entry IS the app default (and degrades to the
      // first curated list in an environment that has no list by that name).
      const core = sortCuratedLists(lists)[0];
      if (!core) {
        setItems([]);
        return false;
      }
      const itemsRes = await getWatchlistItems(core.id, 1, 50);
      commitItems(itemsRes?.data?.items || []);
      setActive({ kind: "id", id: core.id, label: core.name });
      if (persist) {
        // Best-effort repair — the panel is already correct either way.
        setUserDefaultWatchlist({ default_watchlist_id: core.id }).catch(() => {});
      }
      return true;
    } catch (e) {
      setItems([]);
      return false;
    }
  };

  // Mount: load the dropdown's parent + child hierarchy FIRST, then the
  // default items. Strict ordering guarantees that by the time any main-
  // table row is visible, MY WATCHLIST / QUSHi WATCHLIST sub-lists are
  // fully populated — no more "No items" racing the price data.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      // Step 1 — structure (parents + their children).
      try {
        let parentsList = [];
        // Personal lists come from get-user-watchlists — the SAME source the
        // Watchlist screen reads, so this dropdown lists them in the user's own
        // Manage order. (get-watchlists returns them unordered and also leaks
        // legacy parent_id-NULL lists the Watchlist screen doesn't show.)
        const [parentRes, flatRes] = await Promise.all([
          getParentWatchlists().catch(() => null),
          getUserWatchlists().catch(() => null),
        ]);
        if (cancelled) return;

        const parentRaw =
          parentRes?.data?.parentWatchlists ||
          parentRes?.data?.watchlists ||
          parentRes?.data?.data ||
          [];
        parentsList = Array.isArray(parentRaw) ? parentRaw : [];

        const flatRaw =
          flatRes?.data?.watchlists ||
          flatRes?.data?.data ||
          flatRes?.data ||
          [];
        const flatList = Array.isArray(flatRaw) ? flatRaw : [];

        const map = {};
        flatList.forEach((w) => {
          const pid = w?.parent_id;
          if (pid == null) return;
          if (!map[pid]) map[pid] = [];
          map[pid].push(w);
        });

        // Fall back to the per-parent endpoint for any non-direct parent
        // the flat list didn't already hydrate.
        const missing = parentsList
          .filter((p) => !isDirectParent(p.id))
          .filter(
            (p) =>
              !Array.isArray(map[p.id]) || map[p.id].length === 0
          );
        if (missing.length > 0) {
          const fallback = await Promise.all(
            missing.map((p) =>
              getWatchlistsSearchPage(p.id)
                .then((r) => ({
                  parentId: p.id,
                  children:
                    r?.data?.watchlists ||
                    r?.data?.data ||
                    r?.data?.parentWatchlists ||
                    [],
                }))
                .catch(() => ({ parentId: p.id, children: [] }))
            )
          );
          if (cancelled) return;
          fallback.forEach((f) => {
            if (Array.isArray(f.children) && f.children.length > 0) {
              map[f.parentId] = f.children;
            }
          });
        }

        setChildrenByParent(map);
        setParents(parentsList);
      } catch (e) {
        // menu stays empty — still proceed to items so the table loads
      }

      if (cancelled) return;

      // Step 2 — default items (main table).
      try {
        const defaultRes = await getUserDefaultWatchlist().catch(() => null);
        if (cancelled) return;

        // The stored default can point at a personal watchlist the user has
        // since deleted — the items endpoint then 404s and this panel used to
        // render blank. Catch that separately from a real network failure so we
        // only repair a genuinely dangling id.
        let res = null;
        let itemsError = null;
        try {
          res = await getUserDefaultWatchlistItems();
        } catch (e) {
          itemsError = e;
        }
        if (cancelled) return;

        if (itemsError) {
          const dangling = itemsError?.response?.status === 404;
          await loadCoreFallback({ persist: dangling });
          return;
        }

        commitItems(res?.data?.items || []);

        // The server substituted the LEAGUE's own assets, because nothing in the
        // member's chosen list is tradable here. Say so and stop — the branches
        // below would read the null watchlist on that response as "this member
        // has no default at all" and repair it by writing The Core back, which
        // would silently overwrite the league default they had just chosen.
        if (res?.data?.league_assets) {
          setActive({
            kind: "league",
            label: leagueListLabel(league),
          });
          return;
        }

        // Resolve the TITLE from the same response that produced the rows above,
        // so the label can never describe a different list than the one on screen.
        //
        // Only the three legacy string keys are direct categories. A numeric
        // default is a watchlist id and its title is that watchlist's own name —
        // it is NOT looked up against the category parent ids (see the note on
        // DIRECT_LABEL_BY_KEY: The Core is id 3 on live, so that lookup titled
        // the default list "Search Crypto").
        const defaultId = defaultRes?.data?.default_watchlist_id;
        const directKey =
          defaultId == null ? "" : String(defaultId).trim().toLowerCase();

        if (DIRECT_LABEL_BY_KEY[directKey]) {
          setActive({
            kind: "directKey",
            key: directKey,
            label: DIRECT_LABEL_BY_KEY[directKey],
          });
        } else {
          const wl = res?.data?.watchlist;
          if (wl?.id != null) {
            setActive({ kind: "id", id: wl.id, label: wl.name || "" });
          } else {
            // No default of any kind (brand-new user, or one whose only list was
            // deleted) — the curated "The Core" is the default, never a blank.
            await loadCoreFallback({ persist: true });
          }
        }
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Mount-only: loadCoreFallback closes over nothing but setters.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // REMOVED — the "later refresh" in the client's bug report.
  //
  // An effect used to sit here that, whenever the panel settled on zero rows
  // inside an "All <category>" league, quietly fetched that market's curated
  // list and setItems()'d it WITHOUT touching `active`. That is the exact
  // sequence Rev 2 describes: "Since Crypto contains zero eligible assets, the
  // Trade screen initially returns No items. A later refresh then repopulates
  // the asset box from the league's default Stocks source. But the active list
  // label is never corrected, so you end up with Crypto in the header while
  // looking at the default Stocks search list."
  //
  // The panel now shows what the chosen list actually contributes, and the
  // choice itself is gated so that "nothing" cannot be chosen.

  // Ask how many rows of each offered list this league can trade, so the
  // dropdown can grey out the ones that would contribute nothing.
  //
  // ON OPEN, not on mount. Doing it at mount fired one request per list in
  // parallel with the panel's own default-watchlist-items call and cost that
  // call its response — the panel came up empty and unlabelled. This is a list
  // of things the user is in the middle of choosing between, so the moment they
  // open the menu is both early enough and free of anything to race.
  //
  // Outside a league (or in an unrestricted one) fetchEligibleCounts resolves
  // without a single request, so the main Trade screen is untouched.
  useEffect(() => {
    if (!selectorOpen) return undefined;
    if (!league?.game) return undefined;
    const ids = parents
      .filter((p) => !isDirectParent(p.id))
      .flatMap((p) => (childrenByParent[p.id] || []).map((c) => c?.id))
      .filter((id) => id != null);
    if (!ids.length) return undefined;

    let cancelled = false;
    fetchEligibleCounts(league.game, ids, (id, count) => {
      if (!cancelled) setEligibleCounts((prev) => ({ ...prev, [id]: count }));
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectorOpen, league, parents, childrenByParent]);


  // Fetch each row's Last/Chg/Chg% from the chart details endpoint ONE AT A TIME
  // (await each before starting the next) so requests never pile up as a burst of
  // concurrent "pending" calls. As soon as a full pass over all rows finishes, the
  // next pass starts immediately — continuous rolling refresh, always exactly one
  // request in flight. (The selected symbol streams via websocket.)
  useEffect(() => {
    if (!items || items.length === 0) return;
    let cancelled = false;
    let timerId = null;
    // A single request must never hang the whole sequential chain, so cap each
    // fetch at 8s — on timeout we skip that row and move to the next.
    const withTimeout = (promise, ms) =>
      Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
      ]);
    const runPass = async () => {
      for (const it of items) {
        if (cancelled) return;
        const symbol = it?.symbol;
        if (!symbol) continue;
        try {
          const res = await withTimeout(
            getChartStockDetails({
              ticker: symbol,
              category: getCategoryFromSymbol(symbol),
            }),
            8000
          );
          if (cancelled) return;
          const t = res?.data?.data?.ticker;
          if (t) {
            setPriceMap((prev) => {
              const old = prev[symbol];
              // Same numbers as last pass (market closed, quiet symbol): keep
              // the same state object so the watchlist does not re-render.
              if (
                old &&
                old.price === t.price &&
                old.change === t.todaysChange &&
                old.changePct === t.todaysChangePerc
              ) {
                return prev;
              }
              return {
                ...prev,
                [symbol]: {
                  price: t.price,
                  change: t.todaysChange,
                  changePct: t.todaysChangePerc,
                },
              };
            });
          }
        } catch (e) {
          // per-row failure is silent — move on to the next symbol
        }
      }
      // Whole list refreshed — start the next pass right away (still one request
      // at a time). setTimeout(…, 0) yields to the event loop so cancel works.
      // While the tab is in the background nobody sees the list, so wait for
      // it to become visible again instead of polling the API nonstop.
      if (cancelled) return;
      if (document.hidden) {
        waitingForVisible = true;
        return;
      }
      timerId = setTimeout(runPass, 0);
    };
    let waitingForVisible = false;
    const onVisibility = () => {
      if (!document.hidden && waitingForVisible && !cancelled) {
        waitingForVisible = false;
        runPass();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    runPass();
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [items]);

  // User explicitly picks an item from the dropdown — refetch items for it.
  const handleSelect = async (next) => {
    // Client Rev 2: "A list with zero eligible assets should not be selectable as
    // the active Trade list… Do not mark that list Active. Do not change the
    // current Trade wheel."
    //
    // The row is already disabled once its count has arrived; this covers the
    // click that beats the prefetch. Everything below — setActive, the fetch,
    // commitItems — is skipped, so the panel keeps the list it is already on.
    if (next?.kind === "id" && league?.game) {
      const count = await fetchEligibleCount(league.game, next.id);
      if (blocksUseInTrade(count)) {
        setEligibleCounts((prev) => ({ ...prev, [String(next.id)]: 0 }));
        toast.error("This list has no assets eligible for this league.");
        return;
      }
    }

    setActive(next);
    setSelectorOpen(false);
    setLoading(true);
    try {
      let res;
      if (next.kind === "default") {
        res = await getUserDefaultWatchlistItems();
      } else if (next.kind === "directKey") {
        res = await getWatchlistItems(next.key, 1, 50);
      } else if (next.kind === "id") {
        res = await getWatchlistItems(next.id, 1, 50);
      }
      commitItems(res?.data?.items || []);
    } catch (e) {
      // The dropdown can hold a list that was deleted elsewhere (another tab, the
      // Watchlist screen) — 404 means gone, so land on The Core instead of blank.
      if (e?.response?.status === 404) {
        await loadCoreFallback();
      } else {
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const isActive = (opt) => {
    if (active.kind !== opt.kind) return false;
    if (opt.kind === "directKey") return active.key === opt.key;
    if (opt.kind === "id") return active.id === opt.id;
    return true;
  };

  const activeName = active?.label || "Watchlist";

  const handleRowClick = (it) => {
    const symbol = it.symbol;
    if (!symbol || !onStockSelect) return;
    onStockSelect({ symbol, category: getCategoryFromSymbol(symbol) });
  };

  const ticker = stockData?.ticker;
  const livePrice = useLivePrice(ticker?.ticker || ticker?.symbol || selectedStock);

  const apiPrice = ticker?.price ?? ticker?.last ?? ticker?.close;
  const apiChange = ticker?.todaysChange ?? ticker?.change ?? ticker?.dayChange;
  const apiChangePct =
    ticker?.todaysChangePerc ??
    ticker?.change_percent ??
    ticker?.dayChangePercent;

  // Price updates live from the websocket, and change/change% are derived from
  // that same price against the API's previous close — client: "A price and a %
  // change taken from different numbers is a bug." The API's frozen values are
  // only a fallback when there is no previous close.
  const displayPrice =
    livePrice !== null && Number.isFinite(livePrice) ? livePrice : Number(apiPrice);
  const prevClose = Number(ticker?.prev_close);
  const hasBase = prevClose > 0 && displayPrice > 0;
  const displayChange = hasBase ? displayPrice - prevClose : Number(apiChange);
  const displayChangePct = hasBase
    ? ((displayPrice - prevClose) / prevClose) * 100
    : Number(apiChangePct);

  const isUp = Number.isFinite(displayChange) ? displayChange >= 0 : true;
  // Precision for the selected-instrument quote block, from the same rules as the
  // rows above (and the same rules the chart header and order ticket already use).
  const quoteDp = priceDp(
    ticker?.ticker || ticker?.symbol || selectedStock,
    displayPrice
  );
  const displayName = instrumentName({ name: ticker?.name }) || ticker?.name;

  // Live market open/closed for the selected asset's class (crypto = 24/7,
  // forex = weekday sessions, stocks = regular US hours). Re-evaluated when the
  // symbol changes and every 30s so it flips as the session opens/closes.
  const selectedSymbol = ticker?.ticker || ticker?.symbol || selectedStock;
  const [market, setMarket] = useState({ open: true, label: "Market open" });
  useEffect(() => {
    const update = () => setMarket(marketStatus(getCategoryFromSymbol(selectedSymbol)));
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, [selectedSymbol]);

  return (
    <aside
      className={`flex flex-col h-full min-h-0 overflow-hidden border-l border-gray-200 bg-white ${isCollapsed ? "w-72" : "w-80"}`}
      id="chart-watchlist-guide"
    >
      {/* Three fixed zones, one scroll region. The panel reserves the ticket and
          the disclaimer first; the watchlist takes whatever vertical space is
          left (flex-1) and scrolls INSIDE its own box. A list of 5 and a list of
          50 therefore produce the identical panel layout — only the internal
          scroll area differs, and nothing below can ever be pushed off. */}

      {/* Zone 1 — watchlist (the only scrolling region) */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Header row: selector + add. h-10 matches the chart toolbar's 40px band
            height exactly, so the label and the plus land on the same baseline as
            the Share icon and the toolbar controls across the divider. */}
        <div className="flex items-center justify-between px-3 h-10 shrink-0 border-b border-gray-200">
          <div className="relative">
            {/* Switchable inside a league too. It used to be disabled there,
                because the league's fixed list was the only thing this panel
                could show. Client: "Users can continue creating and managing
                their normal personal watchlists" — so the member picks a list
                here exactly as they do on the main site, and the league narrows
                what that list contributes rather than replacing it. */}
            <button
              type="button"
              id="chart-guide-watchlist-selector"
              onClick={() => setSelectorOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-800 hover:text-primaryColor"
            >
              {activeName}
              <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
            </button>
            {selectorOpen && (
              <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[60vh] overflow-y-auto py-1">
              {parents.map((p) => {
                // "Search Stocks / Forex / Crypto" options removed — asset search
                // is handled by the symbol pill in the chart toolbar now. This
                // dropdown only picks watchlists.
                if (isDirectParent(p.id)) return null;
                // Grouped parent (My Watchlist / Qushi Watchlist) with children.
                // The curated group is ordered by the shared helper so this
                // dropdown reads in the SAME order as the Watchlist screen's
                // QUSHi CURATED LISTS column (The Core first). Personal lists
                // keep the user's Manage order straight from the API.
                const raw = childrenByParent[p.id] || [];
                const children =
                  Number(p.id) === CURATED_PARENT_ID ? sortCuratedLists(raw) : raw;
                const isQushi =
                  typeof p.name === "string" &&
                  p.name.toLowerCase().includes("qushi");
                return (
                  <div key={`group-${p.id}`} className="mt-1">
                    {isQushi ? (
                      <div className="px-4 py-1.5 flex items-center gap-1 border-t border-gray-100">
                        <img
                          src={qushiLogo}
                          alt="Qushi"
                          className="h-3.5 object-contain"
                        />
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Watchlist</span>
                      </div>
                    ) : (
                      <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 border-t border-gray-100 text-gray-500">
                        {p.name}
                      </div>
                    )}
                    {children.length === 0 && (
                      <div className="px-6 py-1.5 text-xs text-gray-400 italic">
                        No items
                      </div>
                    )}
                    {children.map((c) => {
                      const opt = {
                        kind: "id",
                        id: c.id,
                        label: c.name,
                      };
                      const activeRow = isActive(opt);
                      // Client Rev 2: a list this league cannot trade a single
                      // row of is not an option, and says why rather than
                      // failing silently when pressed.
                      const blocked = blocksUseInTrade(eligibleCounts[String(c.id)]);
                      return (
                        <button
                          key={`child-${c.id}`}
                          type="button"
                          disabled={blocked}
                          aria-disabled={blocked || undefined}
                          onClick={() => handleSelect(opt)}
                          className={`w-full text-left pl-8 pr-4 py-2 text-sm transition-colors ${
                            blocked
                              ? "cursor-not-allowed text-gray-400"
                              : activeRow
                              ? "text-primaryColor font-semibold"
                              : "text-gray-800 hover:bg-gray-50"
                          }`}
                        >
                          {c.name}
                          {blocked && (
                            <span className="mt-0.5 block text-[11px] leading-snug text-gray-400">
                              {NO_ELIGIBLE_ASSETS_MESSAGE}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onAddToWatchlist}
          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-primaryColor hover:bg-gray-50"
          title="Add to watchlist"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

        {/* Column header */}
        <div className="grid grid-cols-[1fr_70px_60px_60px] shrink-0 items-center px-3 py-1.5 text-[11px] font-semibold text-gray-500 border-b border-gray-200">
          <span>Symbol</span>
          <span className="text-right">Last</span>
          <span className="text-right">Chg</span>
          <span className="text-right">Chg%</span>
        </div>

        {/* Rows (scrollable) */}
        <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && (
          <ListSkeleton
            rows={8}
            avatar={false}
            columns={3}
            columnWidths={[44, 40, 40]}
            divided={false}
            rowClassName="px-3 py-2"
            label="Watchlist loading"
          />
        )}
        {!loading && items.length === 0 && (
          <div className="px-3 py-4 text-xs text-gray-500">No items</div>
        )}
        {!loading && items.map((it, idx) => {
          const info = priceMap[it.symbol];
          const hasData = info !== undefined;
          const lastNum = hasData ? Number(info.price) : NaN;
          const chgNum = hasData ? Number(info.change) : NaN;
          const pctNum = hasData ? Number(info.changePct) : NaN;
          const up = Number.isFinite(chgNum) ? chgNum >= 0 : true;
          // Per-row precision: this row's own symbol and price, not the selected one.
          const dp = priceDp(it.symbol, lastNum);
          return (
            <button
              type="button"
              key={`${it.symbol}-${idx}`}
              onClick={() => handleRowClick(it)}
              className={`w-full grid grid-cols-[1fr_70px_60px_60px] items-center px-3 py-2 text-xs border-b border-gray-100 hover:bg-gray-50 ${
                it.symbol === selectedStock ? "bg-gray-50" : ""
              }`}
            >
              <span className="font-semibold text-gray-800 truncate text-left">
                {baseSymbol(it.symbol)}
              </span>
              <span className="text-right text-gray-800">
                {!hasData ? (
                  <PendingDot />
                ) : Number.isFinite(lastNum) ? (
                  formatNumber(lastNum, dp)
                ) : (
                  "-"
                )}
              </span>
              <span
                className={`text-right ${
                  Number.isFinite(chgNum)
                    ? up
                      ? "text-green-600"
                      : "text-red-500"
                    : "text-gray-400"
                }`}
              >
                {!hasData ? (
                  <PendingDot />
                ) : Number.isFinite(chgNum) ? (
                  `${up ? "+" : ""}${formatNumber(chgNum, dp)}`
                ) : (
                  "-"
                )}
              </span>
              <span
                className={`text-right ${
                  Number.isFinite(pctNum)
                    ? up
                      ? "text-green-600"
                      : "text-red-500"
                    : "text-gray-400"
                }`}
              >
                {!hasData ? (
                  <PendingDot />
                ) : Number.isFinite(pctNum) ? (
                  `${up ? "+" : ""}${formatNumber(pctNum)}%`
                ) : (
                  "-"
                )}
              </span>
            </button>
          );
        })}
      </div>
      </div>

      {/* Zone 2 — quote block + order ticket. Fixed furniture: shrink-0 and no
          scroller of its own, so the primary action on this screen is always
          visible no matter how long the watchlist above it is. */}
      <div className="shrink-0 border-t-[3px] border-gray-200">
        {/* Selected stock info card. pb-0 keeps the "Market open" line close to
            the Sell/Buy row (the panel supplies the gap below it). */}
        {ticker && (
          <div className="px-3 pt-3 pb-0 text-xs">
            {displayName && (
              <div className="text-gray-600 mb-1 truncate" title={ticker.name}>
                {displayName}
              </div>
            )}
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-gray-900">
              {formatNumber(displayPrice, quoteDp)}
            </span>
            <span className="text-[10px] text-gray-500">USD</span>
          </div>
          {Number.isFinite(displayChange) && (
            <div
              className={`text-xs font-medium mt-0.5 ${
                isUp ? "text-green-600" : "text-red-500"
              }`}
            >
              {isUp ? "+" : ""}
              {formatNumber(displayChange, quoteDp)}
              {Number.isFinite(displayChangePct) && (
                <>
                  {" "}
                  ({isUp ? "+" : ""}
                  {formatNumber(displayChangePct)}%)
                </>
              )}
            </div>
          )}
          <div className="flex items-center gap-1 mt-2 text-[10px] text-gray-500">
            <span
              className={`w-1.5 h-1.5 rounded-full inline-block ${
                market.open ? "bg-green-500" : "bg-red-500"
              }`}
            />
            {market.label}
          </div>
        </div>
        )}

        {/* Inline Buy/Sell form */}
        <InlineBuySellPanel
          symbol={
            ticker?.ticker || ticker?.symbol || selectedStock
          }
          editOrder={editOrder}
          onEditOrderDone={onEditOrderDone}
        />
      </div>

      {/* Zone 3 — disclaimer, pinned to the PANEL floor (a direct child of the
          aside, not the tail of the ticket's content flow) with a hairline
          divider above. Visible at all times. */}
      <div className="shrink-0 border-t border-gray-200 px-2 py-2 text-center text-black/60">
        <p className="text-[10px] leading-tight whitespace-nowrap">
          This is live simulated trading with virtual cash.
        </p>
        <p className="text-[9px] leading-tight whitespace-nowrap">
          No brokerage order is sent, and nothing here is financial advice.
        </p>
        <p className="text-[9px] leading-tight whitespace-nowrap">
          Learn in real time without risking real money.
        </p>
      </div>
    </aside>
  );
};

export default ChartRightWatchlist;
